import React, { useMemo } from "react";
import { Input, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

interface EmailTemplateVariableInputProps {
  template: any;
  value: Record<string, string>;
  onChange: (variables: Record<string, string>) => void;
  previousContextFields?: any[];
}

const SYSTEM_VARS = new Set([
  "companyName",
  "productName",
  "logoUrl",
  "year",
  "supportEmail",
  "hrEmail",
  "footerNote",
]);

const extractVariables = (template: any): string[] => {
  if (!template) return [];
  const sources: string[] = [template.subject || "", template.body_html || ""];
  const re = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g;
  const seen = new Set<string>();
  for (const src of sources) {
    let m;
    while ((m = re.exec(src)) !== null) {
      const name = m[1];
      if (SYSTEM_VARS.has(name)) continue;
      if (name.startsWith("trigger.") || name.startsWith("steps.")) continue;
      seen.add(name);
    }
  }
  return Array.from(seen).sort();
};

const humanize = (name: string) =>
  name
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const EmailTemplateVariableInput: React.FC<EmailTemplateVariableInputProps> = ({
  template,
  value = {},
  onChange,
  previousContextFields = [],
}) => {
  const variables = useMemo(() => extractVariables(template), [template]);

  if (!template) {
    return (
      <div style={{ color: "#94a3b8", fontSize: 13, padding: "8px 0" }}>
        Select an email template to map its variables.
      </div>
    );
  }

  if (variables.length === 0) {
    return (
      <div style={{ color: "#475569", fontSize: 13, padding: "8px 0" }}>
        This template has no variables to map — it will send as-is.
      </div>
    );
  }

  const handleChange = (varName: string, newValue: string) => {
    onChange({ ...value, [varName]: newValue });
  };

  return (
    <div className="template-variables-input">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 10,
          fontWeight: 600,
          fontSize: 13,
          color: "#0f172a",
        }}
      >
        Template Variables
        <Tooltip title="Type a static value, or use a context variable like {{trigger.employee_name}} to pull from a previous step.">
          <InfoCircleOutlined style={{ color: "#94a3b8", marginLeft: 8 }} />
        </Tooltip>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {variables.map((varName) => (
          <div key={varName}>
            <div style={{ marginBottom: 4 }}>
              <Tag color="blue" style={{ fontFamily: "monospace", fontSize: 11 }}>
                {`{{${varName}}}`}
              </Tag>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {humanize(varName)}
              </span>
            </div>
            <Input
              placeholder={`Enter value for {{${varName}}} or use a {{variable}}`}
              value={value[varName] || ""}
              onChange={(e) => handleChange(varName, e.target.value)}
              allowClear
            />
          </div>
        ))}
      </div>

      {previousContextFields.length > 0 ? (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: "#64748b",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 6,
            padding: "8px 10px",
          }}
        >
          💡 Available variables:{" "}
          {previousContextFields
            .slice(0, 3)
            .map((f) => (
              <code
                key={f.key}
                style={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  background: "#eef2ff",
                  color: "#3730a3",
                  padding: "1px 6px",
                  borderRadius: 4,
                  marginRight: 4,
                }}
              >{`{{${f.key}}}`}</code>
            ))}
          {previousContextFields.length > 3
            ? ` and ${previousContextFields.length - 3} more`
            : null}
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>
          No upstream variables yet — type static values, or add a trigger / earlier action node.
        </div>
      )}
    </div>
  );
};

export default EmailTemplateVariableInput;
